import { useTranslation } from 'react-i18next';
import { Text, Title, UnstyledButton } from '@/shared/ui';
import { blockCardStyle } from '@/shared/ui/components/composed/settings-block/settings-block.styles';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { InvoiceItem } from '@services/requests/housien-services/AgenciesService.subscription';
import { PlanIcon } from './plan-tab.icons';

interface Props {
  invoices: InvoiceItem[];
  total: number;
  onSeeAll: () => void;
}

const STATUS_TONE: Record<string, string> = {
  paid: 'var(--accent-salvia)',
  pending: 'var(--accent-mostarda)',
  overdue: 'var(--accent-cobre)',
  cancelled: 'var(--text-tertiary-soft)',
  refunded: 'var(--text-tertiary-soft)',
};

/**
 * The last few invoices: date, description, amount, state, download.
 *
 * Widths are in `ch` over tabular figures, which is what the prototype's pixel
 * widths were approximating — they stay aligned if the type scale moves.
 *
 * @param props - Component props.
 * @returns The invoices card.
 */
export function InvoicesList({ invoices, total, onSeeAll }: Props) {
  const { t } = useTranslation('features/pricing');

  return (
    <div className="flex flex-col gap-(--space-2-5)" data-testid="invoices-list">
      <div className="flex items-baseline justify-between gap-(--space-3)">
        <Title order={2} style={{
          margin: 0, fontSize: 'var(--text-app-section)',
          fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
        }}>
          {t('subscription.plan.invoices.title')}
        </Title>
        {total > 0 && (
          <UnstyledButton onClick={onSeeAll} data-testid="see-all-invoices" style={{
            fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
            color: 'var(--seed-base)', textDecoration: 'underline',
            textUnderlineOffset: '3px', whiteSpace: 'nowrap',
          }}>
            {t('subscription.plan.invoices.seeAll', { count: total })}
          </UnstyledButton>
        )}
      </div>

      <div style={blockCardStyle}>
        {invoices.length === 0 ? (
          <Text component="p" data-testid="invoices-empty" style={{
            margin: 0, padding: 'var(--space-4) var(--space-4)',
            fontSize: 'var(--text-body-sm)', color: 'var(--text-tertiary-soft)',
          }}>
            {t('subscription.plan.invoices.empty')}
          </Text>
        ) : invoices.map((invoice) => (
          <InvoiceRow key={invoice.invoice_id} invoice={invoice} />
        ))}
      </div>
    </div>
  );
}

/**
 * One invoice row.
 *
 * @param props - The invoice to render.
 * @returns The row.
 */
function InvoiceRow({ invoice }: { invoice: InvoiceItem }) {
  const { t } = useTranslation('features/pricing');
  const Download = PlanIcon.download;
  const date = formatLocalizedDate(invoice.date);
  const url = invoice.nfse_url || invoice.invoice_url;

  return (
    <div
      className="flex items-center gap-(--space-4)"
      data-testid={`invoice-row-${invoice.invoice_id}`}
      style={{
        padding: 'var(--space-2-5) var(--space-4)',
        borderBottom: '1px solid var(--border-ultra-subtle)',
      }}
    >
      <Text component="span" style={{
        fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)',
        width: '10ch', flex: 'none', fontVariantNumeric: 'tabular-nums',
      }}>
        {date}
      </Text>
      <Text component="span" className="flex-1 min-w-0 truncate" style={{
        fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
      }}>
        {formatCents(invoice.amount_cents)}
      </Text>
      <Text component="span" style={{
        fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-semibold)',
        color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums',
        flex: 'none', whiteSpace: 'nowrap',
      }}>
        {formatCents(invoice.amount_paid_cents)}
      </Text>
      <Text component="span" style={{
        fontSize: 'var(--text-meta)',
        color: STATUS_TONE[invoice.status] ?? 'var(--text-tertiary-soft)',
        minWidth: '7ch', textAlign: 'right', flex: 'none',
      }}>
        {t(`subscription.plan.invoices.status.${invoice.status}`, {
          defaultValue: invoice.status,
        })}
      </Text>
      <UnstyledButton
        disabled={!url}
        onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
        aria-label={url
          ? t('subscription.plan.invoices.download', { date })
          : t('subscription.plan.invoices.downloadUnavailable')}
        title={url ? undefined : t('subscription.plan.invoices.downloadUnavailable')}
        data-testid={`invoice-download-${invoice.invoice_id}`}
        style={{
          padding: 'var(--space-1)', flex: 'none', display: 'flex',
          color: 'var(--text-tertiary-soft)', opacity: url ? 1 : 0.4,
        }}
      >
        <Download size={15} strokeWidth={1.75} aria-hidden />
      </UnstyledButton>
    </div>
  );
}
